// Telefonie SP8 (A11/A12): Auto-„Rückruf"-Vorgang beim Follow-up-Mail-Versand.
//
// Wird eine Follow-up-Mail aus dem Anruf-Kontext ERFOLGREICH gesendet
// (`OpenComposeParams.onSent` im ComposeMailWindow), entsteht automatisch ein
// Vorgang „Rückruf <Kontakt>":
//   - TWV = heute + 2 Werktage (dateUtils.addBusinessDays), editierbar im Vorgang.
//   - KEINE Frist.
//   - hauptverantwortlich = [ich] („ein Vorgang für mich selbst"); Bearbeiter
//     und zu Informierende bleiben LEER.
//   - Kontaktverantwortlichkeit (KV) wird NICHT hier gesetzt, sondern wie überall
//     read-only aus dem verknüpften Kontakt abgeleitet (ggf. geerbt vom Firmen-AP)
//     — der Vorgang wird dafür an `kunde = { id: contactId }` gehängt.
//   - Anrufdatum/-richtung/-nummer landen in der Erst-Notiz.
//   - Der auslösende Anruf wird an den Vorgang gebunden (`vorgangId`) UND
//     automatisch als erledigt markiert (Entscheidung 59/60).
//
// Entscheidungen (docs/plan-review-2026-07-05-entscheidungen.md):
//   55 Vorgangsart „Rückruf" — hier als gespeicherter Wert gesetzt (das Backend
//      persistiert den rohen String; `artOptions` zeigt ihn im Art-Dropdown an).
//   56 TWV-Feld reicht (kein separates Reminder-System).
//   59 Anruf aus Vorgang-/Follow-up-Aktion auto-erledigen (resolved: true).
//   60 Erledigt-Recht: jeder Sichtberechtigte (Backend-Guard prüft das).
//
// Fehlertoleranz: `createRueckrufVorgangFromCall` wirft NUR, wenn die
// Vorgang-Anlage bzw. die Anruf-Verknüpfung scheitert — der Aufrufer (Compose)
// zeigt dann einen Hinweis, die bereits gesendete Mail bleibt unberührt.
import { vorgangService } from "../../services/vorgangService";
import { vorgangNoteService } from "../../services/vorgangNoteService";
import { telephonyService, type CallLogDto } from "../../services/telephonyService";
import { kontaktAbgleichAnbieten } from "./kontaktInVorgangAbgleich";
import { addBusinessDays, toDateInputValue } from "../../utils/dateUtils";
import { callVorgangNote } from "./callVorgangFlow";
import { counterpartyNumber } from "./telephonyUtils";

/** TWV automatisch +2 Werktage (A11) — ohne Wochenende, im Vorgang anpassbar. */
export const RUECKRUF_TWV_WERKTAGE = 2;

/** Vorgangsart des Rückruf-Vorgangs (Entscheidung 55). */
export const RUECKRUF_VORGANGSART = "Rückruf";

/** Titel „Rückruf <Kontakt>" — Kontaktname, sonst Nummer, sonst „Kontakt". */
export function rueckrufTitel(call: CallLogDto): string {
    const name = (call.contactDisplayName ?? "").trim() || counterpartyNumber(call) || "Kontakt";
    return `Rückruf ${name}`;
}

/** Erst-Notiz: Herkunft (Follow-up-Mail) + Anrufdatum/-richtung/-nummer. */
export function rueckrufNote(call: CallLogDto): string {
    return `Automatisch angelegt nach Follow-up-Mail.\n${callVorgangNote(call)}`;
}

/** Payload für `POST /vorgang` (Form wie buildVorgangPayload in createFlowLogic). */
export interface RueckrufVorgangPayload {
    titel: string;
    vorgangsart: string;
    kunde: { id: number };
    hauptverantwortlich: string[];
    bearbeiter: string[];
    zuInformierende: string[];
    twv: string;
    /* KEINE `frist`-Property (A11: ohne Frist). */
}

/**
 * Baut den Vorgang-Payload (pure, DOM-/Fetch-frei → in Vitest testbar, Muster
 * createFlowLogic.buildVorgangPayload): Titel „Rückruf <Kontakt>",
 * Vorgangsart „Rückruf", KV kommt read-only aus dem Kontakt (`kunde`), TWV =
 * `now` + 2 Werktage, KEINE Frist, `hauptverantwortlich = [ich]`, Bearbeiter &
 * zu Informierende leer. `now` ist injizierbar für deterministische Tests.
 */
export function buildRueckrufVorgangPayload(
    call: CallLogDto,
    currentUserId: string | null,
    now: Date = new Date(),
): RueckrufVorgangPayload {
    return {
        titel: rueckrufTitel(call),
        vorgangsart: RUECKRUF_VORGANGSART,
        kunde: { id: call.contactId as number },
        hauptverantwortlich: currentUserId ? [currentUserId] : [],
        bearbeiter: [],
        zuInformierende: [],
        twv: toDateInputValue(addBusinessDays(now, RUECKRUF_TWV_WERKTAGE)),
    };
}

/**
 * Legt den „Rückruf <Kontakt>"-Vorgang an (A11/A12), verknüpft den Anruf und
 * markiert ihn als erledigt. Gibt den aktualisierten Anruf zurück, damit
 * Liste/Detail sofort nachziehen.
 *
 * Ohne Kontakt (`contactId`) wird KEIN Vorgang angelegt (es gibt weder KV noch
 * einen sinnvollen „Rückruf <Kontakt>"): der ursprüngliche Anruf bleibt dann
 * unverändert zurückgegeben. In der Praxis liegt beim Mail-Aufruf immer ein
 * Kontakt vor (Anrufe ohne Kontakt laufen zuvor durch den Zuweisen-Dialog, SP4).
 */
export async function createRueckrufVorgangFromCall(
    call: CallLogDto,
    currentUserId: string | null,
): Promise<CallLogDto> {
    if (!call.contactId) return call;

    // (1) Vorgang anlegen.
    const vorgang = await vorgangService.createVorgang(
        buildRueckrufVorgangPayload(call, currentUserId) as any,
    );

    // (2) Erst-Notiz best-effort — ein Notiz-Fehler darf den bereits angelegten
    //     Vorgang nicht kippen (nur Logging).
    try {
        await vorgangNoteService.create(vorgang.id, { text: rueckrufNote(call) });
    } catch (e) {
        console.error("[Telefonie] Rückruf-Vorgang: Erst-Notiz fehlgeschlagen", e);
    }

    // (3) Anruf ZUSÄTZLICH an den Vorgang binden UND erledigen (Entscheidung
    //     59, PO 01.10.2026 F1) — bestehende Zuordnungen bleiben stehen.
    const updated = await telephonyService.addZuordnungen(
        call.id,
        [{ vorgangId: vorgang.id, todoId: null }],
        { resolve: true },
    );
    kontaktAbgleichAnbieten(updated.fehlendeKontakte);
    return updated;
}
